import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Zap, Layers, Calendar, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

/**
 * Generic Player's Locker KPI log page (Acceptance / Action / future KPIs).
 *
 * Props:
 *  - kpi: 'acceptance' | 'action'
 *  - title: e.g. 'How accepting were you today?'
 *  - subtitle: short helper
 *  - eyebrow: tiny breadcrumb-style label
 *  - icon: lucide icon component
 *  - apiPath: '/locker/acceptance' or '/locker/action'
 */
const ROUND_TYPES = [
  { key: 'practice', label: 'Practice' },
  { key: 'casual', label: 'Casual' },
  { key: 'competitive', label: 'Competitive' },
  { key: 'tournament', label: 'Tournament' },
];

const ScaleRow = ({ value, onChange, onToggleNA, label, help, testid, onRemove }) => {
  const isNA = value === null;
  return (
    <div className="py-3 border-b border-[#F2F0E9] last:border-0" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1A1A1A]">{label}</p>
          {help && <p className="text-xs text-[#737373] mt-0.5">{help}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isNA && (
            <span className="text-lg font-['Playfair_Display'] text-[#1A2E1D] tabular-nums">
              {value || 0}<span className="text-xs text-[#737373]"> / 10</span>
            </span>
          )}
          {isNA && (
            <span className="text-xs uppercase tracking-wider font-semibold text-[#A8A29E]">N/A</span>
          )}
          {onToggleNA && (
            <button
              type="button"
              onClick={onToggleNA}
              className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border transition-colors ${
                isNA
                  ? 'bg-[#1A2E1D] text-white border-[#1A2E1D]'
                  : 'bg-white text-[#737373] border-[#E7E5E4] hover:border-[#1A2E1D]/40'
              }`}
              title={isNA ? 'Mark as relevant again' : "Mark N/A — won't count toward your average"}
              data-testid={`${testid}-na-toggle`}
            >
              N/A
            </button>
          )}
          {onRemove && (
            <button type="button" onClick={onRemove} className="text-[#BC562B] hover:text-[#9A4520]" title="Remove custom dimension">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <input
        type="range"
        min={1}
        max={10}
        step={1}
        value={isNA ? 5 : (value || 5)}
        disabled={isNA}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className={`w-full accent-[#2C5530] ${isNA ? 'opacity-30 cursor-not-allowed' : ''}`}
      />
      <div className="flex justify-between text-[10px] text-[#A8A29E] mt-1 px-0.5">
        <span>Low</span><span>5</span><span>High</span>
      </div>
    </div>
  );
};

const KpiLogPage = ({ kpi, title, subtitle, eyebrow, icon: Icon, apiPath }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('quick');
  const [saving, setSaving] = useState(false);
  const [overall, setOverall] = useState(7);
  const [dimensions, setDimensions] = useState({ defaults: [], custom: [] });
  const [sub, setSub] = useState({});
  const [roundType, setRoundType] = useState('');
  const [courseName, setCourseName] = useState('');
  const [score, setScore] = useState('');
  const [notable, setNotable] = useState('');
  const [reflection, setReflection] = useState('');
  const [roundDate, setRoundDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [newDim, setNewDim] = useState('');
  const [addingDim, setAddingDim] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get(`/locker/dimensions?kpi=${kpi}`);
        setDimensions(data);
        // Initialize all sub_score keys to 7
        const init = {};
        [...(data.defaults || []), ...(data.custom || [])].forEach((d) => { init[d.key] = 7; });
        setSub(init);
      } catch {
        // ignore
      }
    })();
  }, [kpi]);

  const allDims = useMemo(
    () => [...(dimensions.defaults || []), ...(dimensions.custom || []).map((d) => ({ ...d, custom: true }))],
    [dimensions]
  );

  const subAvg = useMemo(() => {
    if (mode !== 'detailed') return null;
    const vals = Object.values(sub).filter((v) => typeof v === 'number');
    if (!vals.length) return 0;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }, [mode, sub]);

  const naCount = useMemo(
    () => Object.values(sub).filter((v) => v === null).length,
    [sub]
  );

  const handleAddDim = async () => {
    const label = newDim.trim();
    if (!label) return;
    setAddingDim(true);
    try {
      const { data } = await API.post('/locker/dimensions', { kpi, label });
      setDimensions((d) => ({ ...d, custom: [...d.custom, data] }));
      setSub((s) => ({ ...s, [data.key]: 7 }));
      setNewDim('');
      toast.success(`"${data.label}" added.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add dimension.');
    } finally {
      setAddingDim(false);
    }
  };

  const handleRemoveDim = async (id, key) => {
    if (!window.confirm('Remove this custom dimension? Past sessions remain unaffected.')) return;
    try {
      await API.delete(`/locker/dimensions/${id}`);
      setDimensions((d) => ({ ...d, custom: d.custom.filter((x) => x.id !== id) }));
      setSub((s) => { const n = { ...s }; delete n[key]; return n; });
    } catch {
      toast.error('Failed to remove dimension.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        mode,
        round_type: roundType || null,
        course_name: courseName.trim() || null,
        score: score === '' ? null : parseInt(score, 10),
        notable_moments: notable.trim() || null,
        reflection: reflection.trim() || null,
        round_date: roundDate,
      };
      if (mode === 'quick') payload.overall = overall;
      if (mode === 'detailed') {
        // Only include keys that exist as current dimensions AND aren't N/A
        const validKeys = new Set(allDims.map((d) => d.key));
        const filtered = {};
        Object.keys(sub).forEach((k) => {
          if (validKeys.has(k) && typeof sub[k] === 'number') filtered[k] = sub[k];
        });
        if (!Object.keys(filtered).length) {
          toast.error('Mark at least one indicator with a score (not N/A).');
          setSaving(false);
          return;
        }
        payload.sub_scores = filtered;
      }
      await API.post(apiPath, payload);
      toast.success('Logged.');
      navigate('/locker');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid={`${kpi}-log-page`}>
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-3xl flex items-center gap-3">
          <Link to="/locker" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-locker">
            <ArrowLeft className="w-4 h-4" /> Locker
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-3xl">
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4" />} {eyebrow}
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">{title}</h1>
          <p className="text-[#57534E]">{subtitle}</p>
        </div>

        {/* Mode toggle */}
        <div className="flex bg-white border border-[#E7E5E4] p-1 mb-6 max-w-md" role="tablist">
          <button
            type="button"
            onClick={() => setMode('quick')}
            className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors ${
              mode === 'quick' ? 'bg-[#1A2E1D] text-white' : 'text-[#57534E] hover:text-[#1A1A1A]'
            }`}
            data-testid={`${kpi}-mode-quick`}
          >
            <Zap className="w-4 h-4" /> Quick (30s)
          </button>
          <button
            type="button"
            onClick={() => setMode('detailed')}
            className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors ${
              mode === 'detailed' ? 'bg-[#1A2E1D] text-white' : 'text-[#57534E] hover:text-[#1A1A1A]'
            }`}
            data-testid={`${kpi}-mode-detailed`}
          >
            <Layers className="w-4 h-4" /> Detailed (2m)
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white border border-[#E7E5E4] p-6">
            {mode === 'quick' ? (
              <ScaleRow
                value={overall}
                onChange={setOverall}
                label={`Overall ${eyebrow?.split(' · ')[0] || kpi}`}
                help="Across the whole round."
                testid={`${kpi}-scale-overall`}
              />
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">
                  {allDims.length} indicator{allDims.length === 1 ? '' : 's'} · averages to your score
                  {naCount > 0 && <span className="ml-2 text-[#BC562B] normal-case tracking-normal">({naCount} N/A — not counted)</span>}
                </p>
                {allDims.map((d) => (
                  <ScaleRow
                    key={d.key}
                    value={sub[d.key]}
                    onChange={(v) => setSub({ ...sub, [d.key]: v })}
                    onToggleNA={() => setSub({ ...sub, [d.key]: sub[d.key] === null ? 7 : null })}
                    label={d.label}
                    help={d.help}
                    testid={`${kpi}-scale-${d.key}`}
                    onRemove={d.custom ? () => handleRemoveDim(d.id, d.key) : null}
                  />
                ))}

                {/* Add custom dimension inline */}
                <div className="mt-4 pt-4 border-t border-[#F2F0E9] flex items-center gap-2">
                  <input
                    type="text"
                    value={newDim}
                    onChange={(e) => setNewDim(e.target.value)}
                    placeholder="Add your own (e.g. Gratitude)"
                    className="flex-1 text-sm"
                    maxLength={60}
                    data-testid={`${kpi}-new-dim-input`}
                  />
                  <button
                    type="button"
                    onClick={handleAddDim}
                    disabled={addingDim || !newDim.trim()}
                    className="inline-flex items-center gap-1 bg-[#1A2E1D] text-white text-xs font-semibold px-3 py-2 hover:bg-[#0F1F12] disabled:opacity-50"
                    data-testid={`${kpi}-add-dim-btn`}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>

                <p className="text-sm text-[#1A1A1A] mt-4 pt-4 border-t border-[#F2F0E9] flex items-center justify-between">
                  <span className="font-semibold">Overall (avg of scored)</span>
                  <span className="font-['Playfair_Display'] text-2xl text-[#1A2E1D] tabular-nums">
                    {subAvg ?? 0}<span className="text-xs text-[#737373]"> / 10</span>
                  </span>
                </p>
              </>
            )}
          </div>

          {/* Context */}
          <div className="bg-white border border-[#E7E5E4] p-6 space-y-5">
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Round type</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ROUND_TYPES.map((rt) => (
                  <button
                    type="button"
                    key={rt.key}
                    onClick={() => setRoundType(roundType === rt.key ? '' : rt.key)}
                    className={`text-xs font-semibold uppercase tracking-wider px-3 py-2.5 border transition-colors ${
                      roundType === rt.key
                        ? 'bg-[#1A2E1D] text-white border-[#1A2E1D]'
                        : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#1A2E1D]/40'
                    }`}
                    data-testid={`${kpi}-round-type-${rt.key}`}
                  >
                    {rt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> Round date
                </label>
                <input
                  type="date"
                  value={roundDate}
                  onChange={(e) => setRoundDate(e.target.value)}
                  className="w-full"
                  data-testid={`${kpi}-round-date`}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Course (optional)</label>
                <input
                  type="text"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  placeholder="Bristol Golf Club"
                  className="w-full"
                  data-testid={`${kpi}-course-name`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Score (optional)</label>
              <input
                type="number"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                placeholder="Leave blank to keep it mental-only"
                className="w-full max-w-[200px]"
                data-testid={`${kpi}-round-score`}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Notable moments</label>
              <textarea
                value={notable}
                onChange={(e) => setNotable(e.target.value)}
                rows={2}
                placeholder="A moment or two that stood out."
                className="w-full"
                data-testid={`${kpi}-notable-moments`}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Reflection</label>
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                rows={4}
                placeholder="What did you notice? What will you carry forward?"
                className="w-full"
                data-testid={`${kpi}-reflection-text`}
              />
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <Link
              to="/locker"
              className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-[#E7E5E4] text-[#57534E] hover:bg-[#F2F0E9] transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors disabled:opacity-50"
              data-testid={`${kpi}-submit-btn`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Log round'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default KpiLogPage;
